import { InputField } from "@/components/form/inputField.tsx";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
	DialogTrigger,
} from "@/components/ui/dialog";
import { Form } from "@/components/ui/form";
import { useDialogState } from "@/hooks/use-dialog-state";
import { TourDateQueries } from "@/modules/tours/queries/tour-date.queries";
import { zodResolver } from "@hookform/resolvers/zod";
import { Tour } from "@repo/types";
import { format } from "date-fns";
import { FunctionComponent, PropsWithChildren, useState } from "react";
import { DateRange } from "react-day-picker";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import * as z from "zod";

export type CreateTourDateDialogProps = PropsWithChildren<{
	tour: Tour;
}>;

export const CreateTourDateDialog: FunctionComponent<
	CreateTourDateDialogProps
> = (props) => {
	const { children, ...otherProps } = props;
	const dialogState = useDialogState();

	return (
		<Dialog {...dialogState}>
			<DialogTrigger asChild>{children}</DialogTrigger>
			<DialogContent className={"w-auto max-w-2xl"}>
				<DialogHeader>
					<DialogTitle>Create tour date</DialogTitle>
				</DialogHeader>

				{dialogState.open && (
					<CreateTourDialogContent {...otherProps} dialogState={dialogState} />
				)}
			</DialogContent>
		</Dialog>
	);
};

const formValidation = z.object({
	error: z.string().optional().nullable(),
	pillion_amount: z.number().int().positive(),
	single_room_amount: z.number().int().positive(),
	pillion_amount_eur: z.number().int().positive(),
	single_room_amount_eur: z.number().int().positive(),
});

type FormSchema = z.infer<typeof formValidation>;

export const CreateTourDialogContent: FunctionComponent<
	CreateTourDateDialogProps & {
		dialogState: ReturnType<typeof useDialogState>;
	}
> = ({ tour, dialogState }) => {
	const createTourDateMutation = TourDateQueries.useCreateTourDateMutation();

	const form = useForm<FormSchema>({
		defaultValues: {
			error: null,
		},
		resolver: zodResolver(formValidation),
	});

	const [selectedDateRange, setSelectedDateRange] = useState<
		DateRange | undefined
	>(undefined);

	const onSubmit = async ({
		pillion_amount,
		single_room_amount,
		pillion_amount_eur,
		single_room_amount_eur,
	}: FormSchema) => {
		await createTourDateMutation.mutateAsync({
			tour_id: tour.id,
			start: format(selectedDateRange?.from as Date, "yyyy-MM-dd"),
			finish: format(selectedDateRange?.to as Date, "yyyy-MM-dd"),
			pillion_amount,
			single_room_amount,
			pillion_amount_eur,
			single_room_amount_eur,
			status: "INACTIVE",
		});
		dialogState.closeDialog();
		toast.success("Tour date created");
	};

	return (
		<Form {...form}>
			<div className={"flex items-center w-full"}>
				<Calendar
					mode="range"
					min={tour.number_of_days}
					max={tour.number_of_days}
					selected={selectedDateRange}
					onSelect={setSelectedDateRange}
					numberOfMonths={2}
				/>
			</div>
			<div className={"w-full flex gap-2 [&>*]:flex-1"}>
				<InputField
					name={"pillion_amount"}
					label={"Pillion amount (NZD)"}
					type={"number"}
					prefix={"NZ$"}
					placeholder={"2568"}
				/>
				<InputField
					name={"pillion_amount_eur"}
					label={"Pillion amount (EUR)"}
					type={"number"}
					prefix={"€"}
					placeholder={"1300"}
				/>
			</div>
			<div className={"w-full flex gap-2 [&>*]:flex-1"}>
				<InputField
					name={"single_room_amount"}
					label={"Single room amount (NZD)"}
					type={"number"}
					prefix={"NZ$"}
					placeholder={"2568"}
				/>
				<InputField
					name={"single_room_amount_eur"}
					label={"Single room amount (EUR)"}
					type={"number"}
					prefix={"€"}
					placeholder={"1300"}
				/>
			</div>

			<Button
				onClick={form.handleSubmit(onSubmit)}
				type="submit"
				isLoading={form.formState.isSubmitting}
				disabled={
					selectedDateRange?.to == null || selectedDateRange?.from == null
				}
			>
				Create tour date
			</Button>
		</Form>
	);
};
